<template xmlns="http://www.w3.org/1999/html">
  <div :key="$store.getters['resource/currentResource'].key">
    <loader v-if="loading" />
    <b-row v-else>
      <b-col xl="6">
        <b-form novalidate ref="form" :validated="formValidated">
          <card-with-aside :title="fields.account_details.title">
            <template slot="asideLeft">
              <formFields
                      class="account-details-switchers"
                :showSubmitButton="false"
                :fields="fields.switchers_details.items"
                :prefillParams="prefillParams"
              />
            </template>
            <template slot="body">
              <formFields
                @submit="debounceSendUserData"
                @clear-account="openClearUserAccountModal('openClearUserAccountModal', $event)"
                :showClearAccountButton="true"
                :fields="fields.account_details.items"
                :prefillParams="prefillParams"
                :editing="editing"
              />
            </template>
          </card-with-aside>
        </b-form>

        <card-with-aside v-if="editing" :title="fields.tags.title" :asideLeftWidth="0">
          <template slot="body">
            <formFields
              @submit="
                (params) => {
                  postData(`users/${prefillParams.id}/tags`, params.tags);
                }
              "
              :formColumns="9"
              class="px-2"
              :fields="fields.tags.items"
              :labelBlockWith="2"
              :prefillParams="prefillParams"
              :mb="0"
            />
          </template>
        </card-with-aside>

        <card-with-aside
          v-if="editing && isMailchimpActive"
          :title="$t('users.editing.mailchimp_subscriptions')"
          :asideLeftWidth="0"
        >
          <template slot="body">
            <b-row
              v-if="mailchimp_categories.length > 0"
              class="border-primary border-top border-bottom mb-3 py-3 border-info mx-1"
            >
              <b-col
                v-for="(category, key) in mailchimp_categories"
                :key="key"
                md="12"
                class="mb-3"
              >
                <h5>{{ category.title }}</h5>

                <b-row>
                  <b-col v-for="(interest, key) in category.interests" :key="key" md="4">
                    <b-form-checkbox
                      @input="
                        (value) => {
                          userMailchimp.interests[interest.id] = value;
                        }
                      "
                      :checked="
                        mailchimp_interests[interest.id]
                          ? mailchimp_interests[interest.id].subscribed
                          : false
                      "
                    >
                      <span v-html="interest.title"></span>
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <div v-else class="text-muted text-center">{{ $t('global.no_data') }}</div>
          </template>
          <template v-if="mailchimp_categories.length > 0" slot="body-footer">
            <b-btn
              @click="postUserData(`users/${prefillParams.id}/mailchimp`, userMailchimp)"
              class="align-self-end btn-width w-100"
              type="primary"
              >{{ $t('button.update') }}
            </b-btn>
          </template>
        </card-with-aside>

        <card-with-aside
          v-if="editing && isCampaignMonitorActive"
          :title="$t('users.editing.campaign_monitor_subscriptions')"
          :asideLeftWidth="0"
        >
          <template slot="titleButton">
            <b-button
              v-if="!!campaign_monitor.length"
              @click="debounceGetUserCampaignMonitor"
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              >{{ $t('button.synchronize') }}
            </b-button>
          </template>
          <template slot="body">
            <loader v-if="campaign_monitor_loading" :styleObject="{ height: '71px' }" />
            <b-row
              v-else-if="!!campaign_monitor.length"
              class="border-primary border-top border-bottom mb-3 py-3 border-info mx-1"
            >
              <b-col v-for="(checkbox, key) in campaign_monitor" :key="key" md="4">
                <b-form-checkbox
                  :disabled="isCheckboxDisabled(checkbox)"
                  @input="
                    (value) => {
                      handleUserSubscription(value, checkbox);
                    }
                  "
                  v-model="checkbox.updating ? !checkbox.subscribed : checkbox.subscribed"
                >
                  <span :class="isCheckboxDisabled(checkbox) ? 'text-muted' : ''">
                    {{ checkbox.list_name }}
                  </span>
                  <p v-if="checkbox.updating" class="text-muted small mb-0">
                    {{ $t('users.list_is_updating') }}
                  </p>

                  <p v-if="checkbox.confirmed_opt" class="text-muted small mb-0">
                    {{ $t('resources.email_conf_required') }}
                    <i :id="checkbox.list_id" class="fas fa-question-circle"></i>
                  </p>

                  <p v-if="checkbox.unsubscribe_settings" class="text-muted small mb-0">
                    {{ $t('resources.dependency_list', { client_name: checkbox.client_name }) }}
                    <i
                      :id="`dependency_list-${checkbox.list_id}`"
                      class="fas fa-question-circle"
                    ></i>
                  </p>
                </b-form-checkbox>
                <b-tooltip
                  v-if="checkbox.confirmed_opt"
                  :target="checkbox.list_id"
                  triggers="hover"
                  :title="$t('resources.email_conf_tooltip')"
                  placement="top"
                ></b-tooltip>
                <b-tooltip
                  v-if="checkbox.unsubscribe_settings"
                  :target="`dependency_list-${checkbox.list_id}`"
                  triggers="hover"
                  :title="$t('resources.dependency_list_tooltip')"
                  placement="top"
                ></b-tooltip>
              </b-col>
            </b-row>
            <div v-else class="text-muted text-center">{{ $t('global.no_data') }}</div>
          </template>
          <template v-if="!!campaign_monitor.length" slot="body-footer">
            <b-btn
              @click="debounceUpdateCampaignMonitorSubscriptions"
              :disabled="
                !campaignMonitorNewSubscriptions.lists.length &&
                !campaignMonitorOldSubscriptions.lists.length
              "
              class="align-self-end btn-width w-100"
              type="primary"
              >{{ $t('button.update') }}
            </b-btn>
          </template>
        </card-with-aside>

        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.payment_sources')"
          :asideLeftWidth="0"
        >
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.cards"
              ref="cards"
              :url="`/admin/users/${id}/payment-sources`"
              :columns="columns.cards"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('cards', data);
                }
              "
            >

              <template slot="payment_method" slot-scope="props">
                <template v-if="props.row.source_type === 'payment-method'">
                  <span>{{toUpperCaseFirstLetter(props.row.payment_method.type)}}</span>
                </template>
                <template v-else>
                  <span>{{ $t('object_representation.field_labels.card') }}</span>
                </template>
              </template>
              <template slot="card_last4" slot-scope="props">
                <template v-if="props.row.source_type === 'payment-method'">
                  <template v-if="props.row.payment_method.type === 'card'">
                    {{ `*&nbsp;${props.row.payment_method.data.card_last4}` }}
                  </template>
                  <template v-else>
                    -------
                  </template>
                </template>
                <template v-else>
                  {{ `*&nbsp;${props.row.user_card.card_last4}` }}
                </template>
              </template>
              <template slot="expiration_date" slot-scope="props">
                <template v-if="props.row.source_type === 'payment-method'">
                  <template v-if="props.row.payment_method.type === 'card'">
                    <column-date
                        v-if="props.row.payment_method.data.expiration_date"
                        :row="props.row.payment_method.data"
                        column="expiration_date"
                        format="L"
                    />
                  </template>
                  <template v-else>
                    -----------
                  </template>
                </template>
                <template v-else>
                  <column-date
                      v-if="props.row.user_card.expiration_date"
                      :row="props.row.user_card"
                      column="expiration_date"
                      format="L"
                  />
                </template>
              </template>
              <template slot="card_brand" slot-scope="props">

                <template v-if="props.row.source_type === 'payment-method'">
                  <template v-if="props.row.payment_method.type === 'card'">
                    <i :class="`fab fa-cc-${toLowerCase(props.row.payment_method.data.card_brand)} fa-lg`"></i>
                  </template>
                  <template v-else>
                    ---
                  </template>
                </template>
                <template v-else>
                  <i :class="`fab fa-cc-${toLowerCase(props.row.user_card.card_brand)} fa-lg`"></i>
                </template>
              </template>

              <template slot="default_card" slot-scope="props">
                <template v-if="props.row.source_type === 'payment-method'">
                  <span>{{ props.row.default ? $t('global.default') : '' }}</span>
                </template>
              </template>

              <template slot="customer_link" slot-scope="props">
                <template v-if="stripeConnectAccountIdTest || stripeConnectAccountIdLive">
                  <b-button
                      v-if="true"
                      @click="goToCustomerPage(props.row)"
                      variant="outline-primary"
                      class="border-0 p-0 m-0 ml-3"
                      style="height: 34px; width: 34px"
                  >
                    <i class="fa-external-link fa-lg"></i>
                  </b-button>
                </template>
              </template>

            </v-server-table>
            <div v-if="isTableEmpty.cards" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>
      </b-col>

      <b-col xl="6" v-if="editing">
        <card-with-aside :title="$t('users.editing.activity')">
          <template slot="asideLeft">
            <div class="d-flex flex-column mb-3 pl-1">
              <h6 class="text-uppercase text-nowrap mb-0">{{ $t('users.editing.created_at') }}</h6>
              <span class="text-primary">{{
                formatDate(activity.registered_in_resource_at, 'L', true)
              }}</span>
            </div>
            <div class="d-flex flex-column mb-3 pl-1">
              <h6 class="text-uppercase mb-0">{{ $t('users.editing.updated_at') }}</h6>
              <span class="text-primary">{{
                formatDate(activity.last_updated_at, 'L', true)
              }}</span>
            </div>
            <div class="d-flex flex-column mb-3 pl-1">
              <h6 class="text-uppercase text-nowrap mb-0">{{ $t('users.editing.ltv') }}</h6>
              <span class="text-primary">{{ activity.ltv !== '' ? activity.ltv : 'N/A' }}</span>
            </div>
          </template>
          <template slot="body">
            <b-col class="px-0 pr-3" v-if="!loadingActivity">
              <b-row class="py-1">
                <b-col>{{ $t('users.editing.content_viewed') }}</b-col>
                <b-col class="text-right">{{
                  activity.activity_last_30_days.content_viewed
                }}</b-col>
              </b-row>
              <b-row class="py-1">
                <b-col>{{ $t('users.editing.paywall_hits') }}</b-col>
                <b-col class="text-right">{{ activity.activity_last_30_days.paywall_hits }}</b-col>
              </b-row>
              <b-row class="py-1">
                <b-col>{{ $t('users.editing.last_action') }}</b-col>
                <b-col class="text-right">{{
                  formatDate(activity.last_action_at, 'L', true)
                }}</b-col>
              </b-row>
            </b-col>
            <loader v-else-if="loadingActivity" :styleObject="{ height: '160px!important' }" />
            <div v-else class="text-muted text-center">{{ $t('global.no_data') }}</div>
          </template>
        </card-with-aside>

        <!--extra-->

        <!-- <card-with-aside :title="$t('users.editing.extra')" :asideLeftWidth="0">
          <template slot="body">
            <b-row v-if="!isObjectEmpty(prefillParams.extra)" class="mb-4">
              <b-col sm="12">
                <b-row
                  v-for="(item, key, index) in prefillParams.extra"
                  :key="index"
                  :class="[isEven(index) ? 'bg-light' : '', 'py-1', 'mx-1']"
                >
                  <b-col class="text-capitalize">{{ normalizeKey(key) }}:</b-col>
                  <b-col class="font-weight-bold">{{ item }}</b-col>
                </b-row>
              </b-col>
            </b-row>
            <div v-else class="text-muted text-center">{{ $t('global.no_data') }}</div>
          </template>
        </card-with-aside> -->

        <!--extra data-->

        <card-with-aside :title="$t('users.editing.extra_data')" :asideLeftWidth="0">
          <template slot="titleButton">
            <b-button
              @click="
                () => {
                  openCreateExtraDataModal('openCreateExtraDataModal');
                }
              "
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              >{{ $t('button.add') }}
            </b-button>
          </template>

          <template slot="body">
            <div v-if="!isObjectEmpty(extraData.groupsObj)">
              <div v-for="(item, key, index) in extraData.groupsObj" :key="index">
                <b-col
                  ><h5 class="text-capitalize">{{ normalizeKey(key) }}:</h5></b-col
                >
                <v-client-table
                  ref="extraData"
                  :data="item"
                  :columns="columns.extraData"
                  :options="options"
                >
                  <template slot="field_key" slot-scope="props">
                    <div style="min-width: 135px">
                      <span>{{ props.row.field_key }}</span>
                    </div>
                  </template>
                  <template slot="field_value" slot-scope="props">
                    <div style="min-width: 135px">
                      <span>{{ props.row.field_value }}</span>
                    </div>
                  </template>
                  <template slot="actions" slot-scope="props">
                    <div class="d-flex align-items-center">
                      <b-button
                        v-if="props.row.group_key && props.row.field_key"
                        @click="
                          openEditExtraDataModal(
                            'openEditExtraDataModal',
                            props.row.group_key,
                            props.row.field_key,
                            props.row.field_value,
                          )
                        "
                        variant="outline-primary"
                        class="ml-md-2 ml-0 border-0 p-1"
                      >
                        <i class="far fa-edit fa-lg"></i>
                      </b-button>
                      <b-button
                        v-if="props.row.group_key && props.row.field_key"
                        @click="openDeleteExtraDataModal(props.row.group_key, props.row.field_key)"
                        variant="outline-primary"
                        class="ml-md-2 ml-0 border-0 p-1"
                      >
                        <i class="far fa-trash-alt fa-lg"></i>
                      </b-button>
                    </div>
                  </template>
                </v-client-table>
              </div>
            </div>

            <div v-else class="text-muted text-center">{{ $t('global.no_data') }}</div>
          </template>
        </card-with-aside>

        <!--subscriptions-->

        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.subscriptions')"
          :asideLeftWidth="0"
        >
          <template slot="titleButton">
            <b-button
              @click="
                () => {
                  $bus.$emit('openChooseSubscriptionModal');
                }
              "
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              >{{ $t('button.add') }}
            </b-button>
          </template>
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.subscriptions"
              ref="subscriptions"
              :url="`/admin/users/${id}/subscriptions/list`"
              :columns="columns.subscriptions"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('subscriptions', data);
                }
              "
            >
              <template slot="title" slot-scope="props">
                <div>
                  <b>{{ $t('users.editing.plan') }}:</b> {{ props.row.plan.title }}<br />
                  <b>{{ $t('users.editing.subscription') }}:</b> {{ props.row.title }}<br />
                  <b>{{ $t('users.editing.ends') }}:</b>
                  {{
                    props.row.subscription_end_date
                      ? formatDate(props.row.subscription_end_date, 'L')
                      : 'N/A'
                  }}<br />
                </div>
              </template>
              <template slot="autorenew" slot-scope="props">
                <column-check :row="props.row" column="autorenew" active="autorenew" />
              </template>
              <template slot="is_trial" slot-scope="props">
                <column-check :row="props.row" column="is_trial" active="is_trial" />
              </template>
              <template slot="actions" slot-scope="props">
                <column-action
                  @edit="
                    openEditSubscriptionModal(
                      'openEditSubscriptionModal',
                      props.row.subscription_end_date,
                      props.row.autorenew,
                      props.row.id,
                      props.row.is_allowed_next_subscription,
                      props.row.is_trial,
                    )
                  "
                  :showDeleteButton="true"
                  :deleteUrl="`/users/${id}/subscriptions/${props.row.id}`"
                  @deleteItems="refreshTable('subscriptions')"
                />
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.subscriptions" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>

        <!-- subscriptions history -->
        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.subscriptions_history')"
          :asideLeftWidth="0"
        >
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.subscriptions_history"
              ref="subscriptions_history"
              :columns="columns.subscriptions_history"
              :options="options"
              :url="`/admin/users/${id}/history/subscriptions/list`"
              @loaded="
                ({ data }) => {
                  handleTableContent('subscriptions_history', data);
                }
              "
            >
              <template slot="is_assigned_by_admin" slot-scope="props">
                <column-check
                  :row="props.row"
                  column="is_attached_by_admin"
                  active="is_attached_by_admin:false"
                />
              </template>
              <template slot="is_trial" slot-scope="props">
                <column-check
                  :row="props.row"
                  column="is_trial_subscription"
                  active="is_trial_subscription"
                />
              </template>
              <template slot="plan.title" slot-scope="props">
                <router-link :to="{ path: `/plans/edit/${props.row.plan_id}` }" target="_blank">{{
                  props.row.plan_title
                }}</router-link>
              </template>
              <template slot="subscription_end_date" slot-scope="props">
                <column-date :row="props.row" column="subscription_end_date" format="L" />
              </template>
              <template slot="subscription_start_date" slot-scope="props">
                <column-date :row="props.row" column="subscription_start_date" format="L" />
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.subscriptions_history" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>

        <!-- tickets -->
        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.tickets_passes')"
          :asideLeftWidth="0"
        >
          <template slot="titleButton">
            <b-button
              @click="
                () => {
                  $bus.$emit('openChooseTicketsModal');
                }
              "
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
            >
              {{ $t('button.add') }}
            </b-button>
          </template>

          <template slot="body">
            <div
              v-for="(event, index) in tievents_for_ticket_table.ti_events"
              :key="index"
              style="border-top: 1px solid #c8ced3"
              class="mb-5 pt-2"
            >
              <h5 class="text-dark">
                <span class="font-weight-light">{{ $t('users.editing.tickets_table.event') }} </span
                >{{ event.title }}
              </h5>
              <h6 class="text-dark">
                <span class="font-weight-light">{{ $t('users.editing.tickets_table.date') }} </span
                >{{ ticketTableDateFormat(event.start_date) }} -
                {{ ticketTableDateFormat(event.end_date) }}
              </h6>
              <div v-for="(ticket, index) in event.tickets" :key="index">
                <div
                  class="justify-content-center"
                  style="border-top: 1px dashed #c8ced3; padding-top: 10px"
                ></div>
                <h6 class="text-dark">
                  <span class="font-weight-light"
                    >{{ $t('users.editing.tickets_table.ticket_title') }} </span
                  >{{ ticket.title }}
                </h6>
                <v-client-table
                  v-show="ticket.passes.length > 0 || ticket.assigns.length > 0"
                  :ref="'passes-' + index"
                  :data="getMergedArrays(ticket.passes, ticket.assigns, 'id')"
                  :columns="columns.tickets"
                  :options="options"
                >
                  <template slot="ticket_owner" slot-scope="props">
                    <span>
                      <router-link
                        :to="{ path: `/users/edit/${props.row.owner_id}`, params: props.row }"
                        target="_blank"
                      >
                        <span
                          class="text-center cursor-pointer"
                          v-if="
                            props.row.owner.first_name !== '' || props.row.owner.last_name !== ''
                          "
                          >{{ props.row.owner.first_name + ' ' + props.row.owner.last_name }}</span
                        >
                        <span class="text-center cursor-pointer" v-else>{{
                          props.row.owner.email
                        }}</span>
                      </router-link>
                    </span>
                  </template>

                  <template slot="ticket_assignee" slot-scope="props">
                    <span class="text-center" v-if="!props.row.assign">{{
                      $t('users.editing.tickets_table.ticket_not_assigned')
                    }}</span>
                    <router-link
                      v-else-if="
                        props.row.assign.first_name !== '' || props.row.assign.last_name !== ''
                      "
                      :to="{ path: `/users/edit/${props.row.assign_id}`, params: props.row }"
                      target="_blank"
                    >
                      <span class="text-center cursor-pointer">{{
                        props.row.assign.first_name + ' ' + props.row.assign.last_name
                      }}</span>
                    </router-link>
                    <router-link
                      v-else
                      :to="{ path: `/users/edit/${props.row.assign_id}`, params: props.row }"
                      target="_blank"
                    >
                      <span class="text-center cursor-pointer">{{ props.row.assign.email }}</span>
                    </router-link>
                  </template>
                  <template slot="magic_link" slot-scope="props">
                    <b-input-group class="d-flex flex-nowrap" style="min-width: 400px">
                      <b-form-input
                        class="rounded-10-left"
                        readonly
                        v-model="props.row.auth_link"
                      ></b-form-input>
                      <b-btn
                        :id="`copyButton-${props.row.auth_link}-${props.row.id}`"
                        @click="copyDataToClipboard(props.row.auth_link, true)"
                        variant="light"
                        size="sm"
                        class="input-height rounded-10-right"
                      >
                        <i class="far fa-copy fa-2x text-muted"></i>
                      </b-btn>
                      <b-btn
                        @click="regenerateAuthLinkForTicket(props.row.id)"
                        variant="light"
                        size="sm"
                        class="input-height rounded-10-right"
                      >
                        <i class="fas fa-sync-alt fa-1x text-muted"></i>
                      </b-btn>
                      <b-btn
                        @click="deleteAuthLinkForTicket(props.row.id)"
                        variant="light"
                        size="sm"
                        class="input-height rounded-10-right"
                      >
                        <i class="fas fa-trash-alt fa-1x text-muted"></i>
                      </b-btn>
                    </b-input-group>
                    <b-tooltip
                      :target="`copyButton-${props.row.auth_link}-${props.row.id}`"
                      triggers="focus"
                      variant=""
                      :title="$t('invite.invite_code_copied')"
                      placement="left"
                      :delay="{ show: 0, hide: 300 }"
                    ></b-tooltip>
                  </template>

                  <template slot="status" slot-scope="props">
                    <b-badge variant="success" v-if="props.row.active">{{
                      $t('global.active')
                    }}</b-badge>
                    <b-badge variant="danger" v-else>{{ $t('global.not_active') }}</b-badge>
                  </template>

                  <template slot="actions" slot-scope="props">
                    <div class="d-flex align-items-center">
                      <b-button
                        v-if="!props.row.assign_id"
                        @click="openAssignTicketModal('openAssignTicketModal', props.row.id)"
                        variant="outline-secondary"
                        size="sm"
                        class="py-0"
                      >
                        {{ $t('button.assign') }}
                      </b-button>
                      <b-button
                        v-if="props.row.assign_id"
                        variant="secondary"
                        size="sm"
                        class="py-0"
                        @click="openRemoveTicketModal(props.row.id, props.index)"
                      >
                        {{ $t('button.detach') }}
                      </b-button>
                      <b-button
                        v-if="props.row.id"
                        @click="openDeleteTicketModal(props.row.id, props.index)"
                        variant="outline-primary"
                        class="ml-md-2 ml-0 border-0 p-1"
                      >
                        <i class="far fa-trash-alt fa-lg"></i>
                      </b-button>
                    </div>
                  </template>
                  <template slot="h__magic_link">
                    <span>{{ $t('users.editing.magic_link') }}</span
                    ><custom-tooltip class="ml-2" variant="" name="magic-link-tooltip"
                      ><div>{{ $t('users.editing.tooltips.magic_link') }}</div></custom-tooltip
                    >
                  </template>


                  <template v-slot:child_row="props">
                    <JsonPrettyTable
                            :jsonData="props.row.extra"
                    />
                  </template>
                </v-client-table>
              </div>
            </div>
            <div
              v-if="tievents_for_ticket_table.ti_events && Object.keys(tievents_for_ticket_table.ti_events).length === 0"
              class="text-muted text-center"
            >
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>

        <!--teams-->

        <card-with-aside v-if="editing" :title="$t('users.editing.teams')" :asideLeftWidth="0">
          <template slot="titleButton">
            <b-button
              @click="
                () => {
                  $bus.$emit('openChooseGroupSubscriptionModal');
                }
              "
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              >{{ $t('button.add') }}
            </b-button>
          </template>
          <template slot="body">
            <v-client-table
              v-show="!isTableEmpty.teams"
              ref="teams"
              :data="teams.items"
              :columns="columns.teams"
              :options="options"
            >
              <template slot="name" slot-scope="props">
                <router-link :to="{ path: `/groups/edit/${props.row.id}`, params: props.row }">
                  {{ props.row.name }}
                </router-link>
              </template>
              <template slot="created_at" slot-scope="props">
                <column-date :row="props.row" column="created_at" format="L" />
              </template>
              <template slot="active" slot-scope="props">
                <column-check :row="props.row" column="active" active="active" />
              </template>
              <template slot="actions" slot-scope="props">
                <column-action
                  :deleteUrl="`/teams/${props.row.id}/users/${id}`"
                  @deleteItems="getUser()"
                  :route="{ path: `/groups/edit/${props.row.id}`, params: props.row }"
                  :showEditButton="false"
                />
              </template>
            </v-client-table>
            <div v-if="isTableEmpty.teams" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>

        <!--sponsored subscriptions-->

        <card-with-aside
                v-if="editing"
                :title="$t('users.editing.sponsored_subscriptions')"
                :asideLeftWidth="0"
        >
          <template slot="titleButton">
            <b-button
                    @click="
                () => {
                  $bus.$emit('openChooseSponsoredSubscriptionModal');
                }
              "
                    variant="outline-secondary"
                    size="sm"
                    class="py-0 ml-2"
            >{{ $t('button.add') }}
            </b-button>
          </template>
          <template slot="body">
            <v-server-table
                    v-show="!isTableEmpty.sponsored_subscriptions"
                    ref="sponsored_subscriptions"
                    :url="`/admin/sponsor/${id}/sponsored-subscriptions`"
                    :columns="columns.sponsored_subscriptions"
                    :options="options"
                    @loaded="
                ({ data }) => {
                  handleTableContent('sponsored_subscriptions', data);
                }
              "
            >
              <template slot="subscription.title" slot-scope="props">
                <div>
                  <b>{{ $t('users.editing.plan') }}:</b> {{ props.row.subscription.plan.title }}<br />
                  <b>{{ $t('users.editing.subscription') }}:</b> {{ props.row.subscription.title }}<br />
                  <b>{{ $t('users.editing.ends') }}:</b>
                  {{
                  props.row.subscription_end_date
                  ? formatDate(props.row.subscription_end_date, 'L')
                  : 'N/A'
                  }}<br />
                </div>
              </template>
              <template slot="slots" slot-scope="props">
                <b>{{props.row.slots.filter(slot => slot.recipient !== null).length}}</b>/<b>{{props.row.slots.length}}</b>
                {{ $t('users.editing.activated') }}
              </template>

              <template slot="is_paying_by_sponsor" slot-scope="props">
                <column-check :row="props.row" column="is_paying_by_sponsor" active="true" />
              </template>
              <template slot="autorenew" slot-scope="props">
                <column-check :row="props.row" column="autorenew" active="autorenew" />
              </template>
              <template slot="is_allowed_next_subscription" slot-scope="props">
                <column-check :row="props.row" column="is_allowed_next_subscription" active="is_allowed_next_subscription" />
              </template>
              <template slot="is_trial" slot-scope="props">
                <column-check :row="props.row" column="is_trial" active="is_trial" />
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.subscriptions" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>

        <!--password sharing-->

        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.password_sharing')"
          :asideLeftWidth="0"
        >
          <template slot="body">
            <v-client-table
              v-if="activity.session_compromised && !loadingActivity"
              ref="session_compromised"
              :data="activity.session_compromised"
              :count="[]"
              :columns="columns.password_sharing"
              :options="options"
            >
              <template slot="session" slot-scope="props">
                <p class="mb-0 text-primary">{{ $t('users.editing.session_compromised') }}</p>
              </template>
            </v-client-table>
            <Switchers
              class="mt-4"
              :fields="fields.switchers.items"
              :prefillParams="prefillParams"
            />
          </template>
        </card-with-aside>

        <!--invites-->

        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.used_invites')"
          :asideLeftWidth="0"
        >
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.invites"
              ref="invites"
              :url="`/admin/users/${id}/invites/list`"
              :columns="columns.invites"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('invites', data);
                }
              "
            >
              <template slot="created_at" slot-scope="props">
                <column-date :row="props.row" column="created_at" format="L" />
              </template>
              <template slot="status" slot-scope="props">
                <b-badge variant="success" v-if="props.row.status === 'sent'"
                  >{{ props.row.status }}
                </b-badge>
                <b-badge variant="danger" v-else>{{ props.row.status }}</b-badge>
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.invites" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>

        <!-- mails -->
        <card-with-aside v-if="editing" :title="$t('users.editing.mail_log')" :asideLeftWidth="0">
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.mails"
              ref="mails"
              :url="`/admin/users/${id}/mails`"
              :columns="columns.mail_log"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('mails', data);
                }
              "
            >
              <template slot="created_at" slot-scope="props">
                <column-date :row="props.row" column="created_at" format="L" />
              </template>
              <template slot="status" slot-scope="props">
                <b-badge variant="success" v-if="props.row.status === 'sent'"
                  >{{ props.row.status }}
                </b-badge>
                <b-badge variant="danger" v-else>{{ props.row.status }}</b-badge>
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.mails" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>
      </b-col>

      <!-- transactions -->
      <b-col cols="12">
        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.transactions')"
          :asideLeftWidth="0"
        >
          <template slot="titleButton">
            <b-button
              @click="
                () => {
                  $bus.$emit('openCreateTransactionModal');
                }
              "
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              >{{ $t('button.add') }}
            </b-button>
          </template>
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.transactions"
              ref="transactions"
              :url="`/admin/users/${id}/transactions/list`"
              :columns="columns.transactions"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('transactions', data);
                }
              "
            >
              <template slot="id" slot-scope="props">
                <router-link
                  v-if="props.row.pay_system === 'manual'"
                  :to="{ path: `/transactions/edit/${props.row.id}`, params: props.row }"
                  target="_blank"
                  >{{ props.row.id }}
                </router-link>
                <span v-else>{{ props.row.id }}</span>
              </template>
              <template slot="amount" slot-scope="props">
                <cents-to-bills :row="props.row" column="amount" :currency="true" />
              </template>
              <template slot="processing_fees" slot-scope="props">
                <cents-to-bills :row="props.row" column="processing_fees" :currency="true" />
              </template>
              <template slot="processing_vat" slot-scope="props">
                <cents-to-bills :row="props.row" column="processing_vat" :currency="true" />
              </template>
              <template slot="created_at" slot-scope="props">
                <column-date :row="props.row" column="created_at" format="L" />
              </template>
              <template slot="status" slot-scope="props">
                <b-badge variant="success" v-if="props.row.status === 'succeeded'">
                  {{ props.row.status }}
                </b-badge>
                <b-badge variant="danger" v-else>{{ props.row.status }}</b-badge>
              </template>
              <template slot="amount" slot-scope="props">
                <cents-to-bills :row="props.row" column="amount" :currency="true" />
              </template>
              <template slot="actions" slot-scope="props">
                <column-action
                  v-if="props.row.pay_system === 'manual'"
                  :deleteUrl="`/transactions/${props.row.id}`"
                  :route="{ path: `/transactions/edit/${props.row.id}`, params: props.row }"
                  :showEditButton="false"
                  @deleteItems="refreshTable('transactions')"
                />
              </template>
              <template slot="card_brand" slot-scope="props">
                <i
                  v-if="props.row.card"
                  :class="`fab fa-cc-${toLowerCase(props.row.card.card_brand)} fa-lg`"
                ></i>
              </template>
              <template slot="card_last4" slot-scope="props">
<!--                <span v-if="props.row.card">{{-->
<!--                  `****&nbsp;****&nbsp;****&nbsp;${props.row.card.card_last4}`-->
<!--                }}</span>-->
                <template v-if="props.row.payment_source && props.row.payment_source.source_type === 'payment-method'">
                  <template v-if="props.row.payment_source.payment_method.type === 'card'">
                    {{ `*&nbsp;${props.row.payment_source.payment_method.data.card_last4}` }}
                  </template>
                  <template v-else>
                    <span>{{toUpperCaseFirstLetter(props.row.payment_source.payment_method.type)}}</span>
                  </template>
                </template>
                <template v-else>
                  <template v-if="props.row.card">
                    {{ `*&nbsp;${props.row.card.card_last4}` }}
                  </template>
                  <template v-else>
                    <span>-------</span>
                  </template>
                </template>
              </template>
              <template slot="receipt_actions" slot-scope="props">
                <column-receipt-actions :transaction="props.row" />
              </template>
              <template slot="refund" slot-scope="props">
                <template v-if="(stripeConnectAccountIdTest || stripeConnectAccountIdLive) && props.row.status === 'succeeded'">
                  <b-button
                      v-if="true"
                      @click="goToTransactionPage(props.row)"
                      variant="outline-primary"
                      class="border-0 p-0 m-0 ml-3"
                      style="height: 34px; width: 34px"
                  >
                    <i class="fa-external-link fa-lg"></i>
                  </b-button>
                </template>
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.transactions" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>
      </b-col>
    </b-row>

    <!-- Sessions Table -->
    <b-row v-if="!loading">
      <b-col cols="12">
        <card-with-aside
          v-if="editing"
          :title="$t('users.editing.content_access')"
          :asideLeftWidth="0"
        >
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.content"
              ref="content"
              :url="`/admin/users/${id}/access-content`"
              :columns="columns.content"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('content', data);
                }
              "
            >
              <template slot="reason" slot-scope="props">
                <span class="text-capitalize">{{ normalizeKey(props.row.reason) }}</span>
              </template>

              <template slot="access" slot-scope="props">
                <column-check :row="props.row" column="access" active="access" />
              </template>

              <template slot="created_at" slot-scope="props">
                <column-date :row="props.row" column="created_at" format="L" />
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.content" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>
      </b-col>

      <b-col cols="12">
        <card-with-aside v-if="editing" :title="$t('users.editing.sessions')" :asideLeftWidth="0">
          <template slot="body">
            <v-server-table
              v-show="!isTableEmpty.sessions"
              ref="sessions"
              :url="`/admin/users/${id}/sessions`"
              :columns="columns.sessions"
              :options="options"
              @loaded="
                ({ data }) => {
                  handleTableContent('sessions', data);
                }
              "
            >
              <template slot="compromised" slot-scope="props">
                <column-check :row="props.row" column="compromised" active="compromised" />
              </template>

              <template slot="active" slot-scope="props">
                <column-check :row="props.row" column="active" active="active" />
              </template>

              <template slot="expires" slot-scope="props">
                <column-date :row="props.row" column="expires" format="L" />
              </template>

              <template slot="created_at" slot-scope="props">
                <column-date :row="props.row" column="created_at" format="L" />
              </template>
            </v-server-table>
            <div v-if="isTableEmpty.sessions" class="text-muted text-center">
              {{ $t('global.no_data') }}
            </div>
          </template>
        </card-with-aside>
      </b-col>
    </b-row>

    <!-- subscription modal-->

    <modal
      v-if="editing"
      name="ChooseSubscription"
      :confirm="$t('button.attach')"
      :title="$t('users.create.subscription')"
      :preventHideOnOk="true"
      @ok="
        postUserData(
          `users/${id}/subscriptions/${subscription.value}`,
          {},
          false,
          'subscriptions',
          'ChooseSubscription',
          'subscription',
        )
      "
    >
      <template slot="body">
        <b-col>
          <group-select
            :model="subscription"
            :input="planInput"
            @select="
              (value) => {
                subscription = value.value;
              }
            "
          >
          </group-select>
        </b-col>
      </template>
    </modal>


    <!-- sponsored subscription modal-->

    <modal
        v-if="editing"
        name="ChooseSponsoredSubscription"
        :confirm="$t('button.attach')"
        :title="$t('users.create.sponsored_subscription')"
        :preventHideOnOk="true"
        @ok="
        postUserData(
          `sponsor/${id}/sponsored-subscriptions`,
          {
            subscription_id: sponsoredSubscription.value,
          },
          false,
          'sponsored_subscriptions',
          'ChooseSponsoredSubscription',
          'sponsoredSubscription',
        )
      "
    >
      <template slot="body">
        <b-col class="">
          <b-form-group label="Sponsored Subscription Type" v-slot="{ ariaDescribedby }">
            <div class="d-flex">
            <b-form-radio
                class="cursor-pointer"
                v-model="sponsoredSubscriptionType"
                :aria-describedby="ariaDescribedby"
                name="sponsored-subscription-types"
                value="gift">
              <span class="cursor-pointer">Gift</span>
            </b-form-radio>
            <b-form-radio
                class="ml-4 cursor-pointer"
                v-model="sponsoredSubscriptionType"
                :aria-describedby="ariaDescribedby"
                name="sponsored-subscription-types"
                value="multi-seat">
              <span class="cursor-pointer">Multi-seat</span>
            </b-form-radio>
            </div>
          </b-form-group>
        </b-col>
        <b-col :key="sponsoredSubscriptionType">
          <group-select
              :model="sponsoredSubscription"
              :input="getSponsoredSubscriptionsInput(sponsoredSubscriptionType)"
              @select="
              (value) => {
                sponsoredSubscription = value.value;
              }
            "
          >
          </group-select>
        </b-col>
      </template>
    </modal>

    <!--group modal-->

    <modal
      v-if="editing"
      name="ChooseGroupSubscription"
      :confirm="$t('button.attach')"
      :title="$t('users.create.team')"
      :preventHideOnOk="true"
      @ok="
        postUserData(
          `teams/${team.value}/users`,
          { user_ids: [id] },
          true,
          null,
          'ChooseGroupSubscription',
          'team',
        )
      "
    >
      <template slot="body">
        <b-row>
          <b-col class="position-relative">
            <label for="team"
              >{{ $t('groups.group_subscription')
              }}<span class="text-danger">{{ $t('form.required_star') }}</span></label
            >
            <multiselect
              class="mb-4"
              v-model="team"
              id="team"
              label="label"
              track-by="label"
              :placeholder="$t('transactions.type_to_search')"
              :options="teamsItems"
              :searchable="true"
              :close-on-select="true"
              :allow-empty="false"
              :preselect-first="true"
              select-label=""
              selected-label=""
              deselect-label="X "
            >
              <template slot="noResult">
                {{ $t('global.no_results') }}
              </template>
            </multiselect>
          </b-col>
        </b-row>
      </template>
    </modal>

    <!--transactions modal-->

    <modal
      v-if="editing"
      name="CreateTransaction"
      :confirm="$t('button.save')"
      :title="$t('users.create.transaction')"
      :preventHideOnOk="true"
      @ok="submit('transactions')"
    >
      <template slot="body">
        <TransactionView
          :fields="transactionFields"
          :columnWith="12"
          :userPrefillId="id"
          :showButtons="false"
          @submit="
            refreshTable('transactions');
            refreshTable('subscriptions');
            refreshTable('content');
          "
        />
      </template>
    </modal>

    <!--edit subscription modal-->

    <modal
      v-if="editing"
      name="EditSubscription"
      :confirm="$t('button.save')"
      :title="$t('users.editing.edit_subscription')"
      :preventHideOnOk="true"
      @ok="submitEditSubscription"
    >
      <template slot="body">
        <b-row class="align-items-end mb-4">
          <b-col class="flex-column">
            <label class="d-block">{{ $t('form.end_date') }}</label>

            <DatePickerInput v-model="subscription.subscription_end_date" />
          </b-col>

          <b-col class="d-flex flex-row">
            <b-form-checkbox type="checkbox" id="autorenew" v-model="subscription.autorenew" />
            <label for="autorenew" class="mr-2">{{ $t('subscripions.autorenew') }}</label>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col class="d-flex flex-row">
            <b-form-checkbox
              type="checkbox"
              id="allowed-next-subscription"
              v-model="subscription.is_allowed_next_subscription"
            />
            <label for="allowed-next-subscription" class="mr-2">{{
              $t('form.next_subscription')
            }}</label>
          </b-col>
          <b-col class="d-flex flex-row">
            <b-form-checkbox
              type="checkbox"
              id="is-trial-subscription"
              v-model="subscription.is_trial"
            />
            <label for="is-trial-subscription" class="mr-2">{{
              $t('form.is_trial')
            }}</label>
          </b-col>
        </b-row>
      </template>
    </modal>

    <!-- clear account modal-->
    <modal
      v-if="editing"
      name="ClearUserAccount"
      :confirm="$t('button.clear_account_confirm')"
      :title="$t('users.clean_up_account')"
      :preventHideOnOk="true"
      @ok="cleanUpUserData($event, 'ClearUserAccount')"
    >
      <template slot="body">
        <b-col class="text-center">
          <h6>This would clean up member’s data from Wallkit, Firebase and connected services.</h6>
          <h5>Please confirm.</h5>
        </b-col>
      </template>
    </modal>

    <!--  Choose tickets modal  -->
    <modal
      v-if="editing"
      name="ChooseTickets"
      title="Choose Tickets"
      :preventHideOnOk="true"
      :confirm="$t('button.add')"
      @ok="assignTicketToUser('ChooseTickets')"
    >
      <template slot="body">
        <b-col>
          <label for="tievents">{{ $t('form.events') }}</label>
          <multiselect
            class="mb-4"
            v-model="tievents"
            id="tievents"
            label="title"
            track-by="title"
            :placeholder="$t('transactions.type_to_search')"
            :options="tieventsItems"
            :searchable="true"
            :close-on-select="true"
            :allow-empty="false"
            :preselect-first="true"
            select-label=""
            selected-label=""
            deselect-label="X "
          >
            <template slot="noResult">
              {{ $t('global.no_results') }}
            </template>
          </multiselect>

          <label for="tievents">{{ $t('form.tickets') }}</label>
          <multiselect
            class="mb-4"
            v-model="tievents_tickets"
            id="tievents_tickets"
            label="title"
            track-by="title"
            :placeholder="$t('transactions.type_to_search')"
            :options="tievents_tickets_items"
            :searchable="true"
            :close-on-select="true"
            :allow-empty="false"
            :preselect-first="true"
            select-label=""
            selected-label=""
            deselect-label="X "
          >
            <template slot="noResult">
              {{ $t('global.no_results') }}
            </template>
          </multiselect>
        </b-col>
      </template>
    </modal>

    <!--  assign ticket modal  -->
    <modal
      v-if="editing"
      name="AssignTicket"
      title="Assign Ticket"
      :preventHideOnOk="false"
      :confirm="$t('button.assign')"
      @ok="
        assignTicket(
          `ti-event/ticket/pass/${ticketId}/user`,
          { user_id: users.value },
          true,
          'tickets',
          'AssignTicket',
          'users',
        )
      "
    >
      <template slot="body">
        <b-row class="no-gutters">
          <b-col class="flex-column">
            <label for="users">{{ $t('users.email') }}</label>
            <multiselect
              class="mb-4"
              v-model="users"
              id="users"
              label="label"
              track-by="label"
              :placeholder="$t('transactions.type_to_search')"
              :options="selectItems"
              :searchable="true"
              :close-on-select="true"
              :allow-empty="false"
              :preselect-first="true"
              select-label=""
              selected-label=""
              deselect-label="X "
              :loading="isLoadingAssignUsers"
              @search-change="onAssignUsersSearch"
            >
              <template slot="noResult">
                {{ $t('global.no_results') }}
              </template>
            </multiselect>
          </b-col>
        </b-row>
      </template>
    </modal>

    <!-- create extra data -->
    <modal
      v-if="editing"
      @ok="submitExtraData('create')"
      name="CreateExtraData"
      :confirm="$t('button.save')"
      :title="$t('users.create.extra_data')"
      :preventHideOnOk="false"
    >
      <template slot="body">
        <!-- Group fields -->
        <b-row class="no-gutters align-items-end mb-0">
          <b-col class="flex-column mb-4">
            <label class="d-block">{{ $t('form.group') }}</label>
            <multiselect
              v-if="!extraData.newGroup"
              v-model="extraData.prefillData.group_key"
              :placeholder="$t('transactions.type_to_search')"
              :options="extraData.groupsKeys"
              :loading="isSelectLoading.group"
              :searchable="true"
              @search-change="
                (query) => {
                  loadExtraDataGroupList(query);
                }
              "
              @open="loadExtraDataGroupList('')"
              :close-on-select="true"
              :allow-empty="true"
              :preselect-first="true"
              select-label=""
              selected-label=""
              deselect-label="X "
            >
              <template slot="noResult">
                {{ $t('global.no_results') }}
              </template>
            </multiselect>
            <FormFieldInput
              v-else
              style="height: 40px"
              v-model="extraData.prefillData.group_key"
              :type="'text'"
              :name="'new_group_key'"
              :id="'new_group_key'"
              :required="true"
              :autocomplete="'off'"
            />
          </b-col>

          <b-col class="flex-column d-flex flex-row ml-4">
            <div class="align-items-center justify-content-start mb-4">
              <label class="pr-0 mb-3 text-uppercase">{{
                $t('users.editing.extra_data_modal.switcher_label')
              }}</label>
              <div>
                <label class="switch switch-3d switch-success mr-2 mb-0">
                  <b-form-checkbox
                    type="checkbox"
                    class="switch-input"
                    v-model="extraData.newGroup"
                  />
                  <input type="checkbox" class="switch-input" :checked="extraData.newGroup" />
                  <span class="switch-slider"></span>
                </label>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Fields inputs -->
        <b-row class="no-gutters align-items-end mb-0">
          <b-col class="flex-column mb-4">
            <label class="d-block">{{ $t('form.key') }}</label>
            <multiselect
              v-if="!extraData.newField"
              v-model="extraData.prefillData.field_key"
              :placeholder="$t('transactions.type_to_search')"
              :options="extraData.groupFields"
              :loading="isSelectLoading.field"
              :searchable="true"
              @search-change="
                (query) => {
                  loadExtraDataGroupFieldList(query);
                }
              "
              :close-on-select="true"
              :allow-empty="true"
              :preselect-first="true"
              select-label=""
              selected-label=""
              deselect-label="X "
            >
              <template slot="noResult">
                {{ $t('global.no_results') }}
              </template>
            </multiselect>
            <FormFieldInput
              v-else
              style="height: 40px"
              v-model="extraData.prefillData.field_key"
              :type="'text'"
              :name="'new_field_key'"
              :id="'new_field_key'"
              :required="true"
              :autocomplete="'off'"
            />
          </b-col>
          <b-col class="flex-column d-flex flex-row ml-4">
            <div class="align-items-center justify-content-start mb-2">
              <label class="pr-0 mb-3 text-uppercase">{{
                $t('users.editing.extra_data_modal.switcher_label_field')
              }}</label>
              <div>
                <label class="switch switch-3d switch-success mr-2 mb-0">
                  <b-form-checkbox
                    type="checkbox"
                    class="switch-input"
                    v-model="extraData.newField"
                  />
                  <input type="checkbox" class="switch-input" :checked="extraData.newField" />
                  <span class="switch-slider"></span>
                </label>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="flex-column flex-row" cols="6">
            <div class="align-items-center justify-content-start mb-4">
              <label class="d-block">{{ $t('form.value') }}</label>
              <FormFieldInput
                style="height: 40px"
                v-model="extraData.prefillData.field_value"
                :type="'text'"
                :name="'new_field_value'"
                :id="'new_field_value'"
                :required="true"
                :autocomplete="'off'"
              />
            </div>
          </b-col>
        </b-row>
      </template>
    </modal>

    <!-- edit extra data -->
    <modal
      v-if="editing"
      @ok="submitExtraData('edit')"
      name="EditExtraData"
      :confirm="$t('button.save')"
      :title="$t('users.editing.edit_extra_data')"
    >
      <template slot="body">
        <b-row class="no-gutters align-items-end mb-0">
          <b-col class="flex-column mb-4">
            <label class="d-block">{{ $t('form.group') }}</label>
            <multiselect
              v-if="!extraData.newGroup"
              v-model="extraData.prefillData.group_key"
              :placeholder="$t('transactions.type_to_search')"
              :options="extraData.groupsKeys"
              @search-change="
                (query) => {
                  loadExtraDataGroupList(query);
                }
              "
              @open="loadExtraDataGroupList('')"
              :searchable="true"
              :close-on-select="true"
              :allow-empty="true"
              :preselect-first="true"
              select-label=""
              selected-label=""
              deselect-label="X "
            >
              <template slot="noResult">
                {{ $t('global.no_results') }}
              </template>
            </multiselect>

            <FormFieldInput
              v-else
              style="height: 40px"
              v-model="extraData.prefillData.group_key"
              :type="'text'"
              :name="'new_group_key'"
              :id="'new_group_key'"
              :required="true"
              :autocomplete="'off'"
            />
          </b-col>
          <b-col class="flex-column d-flex flex-row ml-4">
            <div class="align-items-center justify-content-start mb-4">
              <label class="pr-0 mb-3 text-uppercase">{{
                $t('users.editing.extra_data_modal.switcher_label')
              }}</label>
              <div>
                <label class="switch switch-3d switch-success mr-2 mb-0">
                  <b-form-checkbox
                    type="checkbox"
                    class="switch-input"
                    v-model="extraData.newGroup"
                  />
                  <input type="checkbox" class="switch-input" :checked="extraData.newGroup" />
                  <span class="switch-slider"></span>
                </label>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row class="no-gutters align-items-end mb-0">
          <b-col class="flex-column mb-4">
            <label class="d-block">{{ $t('form.key') }}</label>
            <multiselect
              v-if="!extraData.newField"
              v-model="extraData.prefillData.field_key"
              :placeholder="$t('transactions.type_to_search')"
              :options="extraData.groupFields"
              :loading="isSelectLoading.field"
              :searchable="true"
              @search-change="
                (query) => {
                  loadExtraDataGroupFieldList(query);
                }
              "
              :close-on-select="true"
              :allow-empty="true"
              :preselect-first="true"
              select-label=""
              selected-label=""
              deselect-label="X "
            >
              <template slot="noResult">
                {{ $t('global.no_results') }}
              </template>
            </multiselect>
            <FormFieldInput
              v-else
              style="height: 40px"
              v-model="extraData.prefillData.field_key"
              :type="'text'"
              :name="'new_field_key'"
              :id="'new_field_key'"
              :required="true"
              :autocomplete="'off'"
            />
          </b-col>
          <b-col class="flex-column d-flex flex-row ml-4">
            <div class="align-items-center justify-content-start mb-2">
              <label class="pr-0 mb-3 text-uppercase">{{
                $t('users.editing.extra_data_modal.switcher_label_field')
              }}</label>
              <div>
                <label class="switch switch-3d switch-success mr-2 mb-0">
                  <b-form-checkbox
                    type="checkbox"
                    class="switch-input"
                    v-model="extraData.newField"
                  />
                  <input type="checkbox" class="switch-input" :checked="extraData.newField" />
                  <span class="switch-slider"></span>
                </label>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="flex-column flex-row" cols="6">
            <div class="align-items-center justify-content-start mb-4">
              <label class="d-block">{{ $t('form.value') }}</label>
              <FormFieldInput
                style="height: 40px"
                v-model="extraData.prefillData.field_value"
                :type="'text'"
                :name="'new_field_value'"
                :id="'new_field_value'"
                :required="true"
                :autocomplete="'off'"
              />
            </div>
          </b-col>
        </b-row>
      </template>
    </modal>

    <!-- delete extra data -->
    <modal
      @ok="deleteExtraData"
      @hide="clearDeleteExtraData"
      @cancel="clearDeleteExtraData"
      :showModal="showDeleteExtraDataModal"
    >
      <template slot="body">
        <h3 class="text-center">{{ $t('global.are_you_sure') }}</h3>
      </template>
    </modal>

    <!-- detach ticket -->

    <modal
      name="Remove ticket"
      :confirm="$t('button.detach')"
      title="Remove ticket"
      :showModal="showRemoveTicketModal"
      @ok="removeTicket"
      @cancel="clearRemovableTicket"
      @hide="clearRemovableTicket"
    />

    <!-- delete ticket -->

    <modal
      @ok="deleteTicket"
      @hide="clearDeleteTicket"
      @cancel="clearDeleteTicket"
      :showModal="showDeleteTicketModal"
    >
      <template slot="body">
        <h3 class="text-center">{{ $t('global.are_you_sure') }}</h3>
      </template>
    </modal>
  </div>
</template>

<script>
import formFields from '@/components/global/formFields';
import Switchers from '@/components/global/switchers';
import JsonPrettyTable from '@/components/global/jsonPrettyTable';
import axios from '@/utils/axios';
import i18n from '@/utils/i18n';
import transactionFields from '@/views/transactions/transactionsCreationFields';
import TransactionView from '@/views/transactions/TransactionView';
import { isEmpty, debounce, orderBy, pull, get } from 'lodash';
import DatePickerInput from '@/components/forms/fields/datePickerInput';
import FormFieldInput from '@/components/forms/fields/formFieldInput';
import { formattedDateForBrowsers } from '../../utils/date-utils';
import { inviteMethods } from '../../mixins/invite';
import ColumnReceiptActions from '@/components/table/ColumnReceiptActions';
const NO_TIME_SYMBOL = '--:--';
export default {
  name: 'usersView',
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    inputs: {
      type: Object,
      required: true,
    },
  },
  mixins: [inviteMethods],
  data() {
    return {
      isSelectLoading: {},
      disabledDependencyLists: {},
      formValidated: false,
      loadingActivity: false,
      loading: false,
      prefillParams: {
        active: true,
      },
      extraData: {
        groupsObj: {},
        groupsKeys: [],
        groupFields: [],
        loading: false,
        prefillData: {
          group_key: null,
          field_key: null,
          field_value: null,
        },
        newGroup: false,
        newField: false,
      },
      activity: {},
      tievents: '',
      teamsItems: [],
      tieventsItems: [],
      tievents_tickets: '',
      tievents_tickets_items: [],
      tievents_for_ticket_table: {},
      tickets: [],
      ticketId: null,
      mailchimp_categories: [],
      mailchimp_interests: {},
      campaign_monitor: [],
      campaign_monitor_loading: false,
      fields: [],
      email: '',
      teams: {
        items: [],
      },
      planInput: {
        name: 'subscription_id',
        label: i18n.t('form.subscriptions_id'),
        placeholder: i18n.t('transactions.type_to_search'),
        filter_field: 'subscriptions.title',
        searchable: true,
        group: true,
        allowEmpty: false,
        preselectFirst: true,
        required: true,
        options_url: 'plans?limit=100',
      },
      tableItems: {},
      transactionFields: [],
      contentFields: [],
      subscriptionFields: [],
      selectOptions: {},
      team: {},
      users: [],
      subscription: {},
      sponsoredSubscription: {},
      sponsoredSubscriptionType: 'gift',
      debounceGetOptions: () => {},
      debounceSendUserData: () => {},
      debounceUpdateCampaignMonitorSubscriptions: () => {},
      debounceGetUserCampaignMonitor: () => {},
      userMailchimp: {
        interests: {},
      },
      campaignMonitorOldSubscriptions: {
        lists: [],
      },
      campaignMonitorNewSubscriptions: {
        lists: [],
      },
      paramsMutated: {
        settings: {},
      },
      isTableEmpty: {
        mails: false,
        invites: false,
        subscriptions: false,
        sponsored_subscriptions: false,
        transactions: false,
        subscriptions_history: false,
        teams: false,
        content: false,
        sessions: false,
        tickets: false,
        users: false,
        extraData: false,
      },
      columns: {
        mail_log: ['id', 'created_at', 'subject', 'status'],
        teams: ['name', 'users_count', 'type', 'active', 'actions'],
        invites: ['title', 'code'],
        transactions: [
          'id',
          'card_brand',
          'pay_system',
          'card_last4',
          'amount',
          'processing_fees',
          'processing_vat',
          'created_at',
          'status',
          'receipt_actions',
        ],
        sponsored_subscriptions: ['subscription.title', 'subscription.type', 'slots', 'is_paying_by_sponsor', 'autorenew', 'is_allowed_next_subscription', 'is_trial'],
        subscriptions: ['title', 'autorenew', 'is_trial', 'actions'],
        subscriptions_history: [
          'subscription_title',
          'plan.title',
          'subscription_start_date',
          'subscription_end_date',
          'is_trial',
          'is_assigned_by_admin',
        ],
        content: [
          'content_id',
          'session_id',
          'content_title',
          'content_key',
          'reason',
          'access',
          'created_at',
        ],
        sessions: [
          'id',
          'session',
          'ip',
          'requests',
          'device',
          'browser',
          'platform',
          'expires',
          'created_at',
          'active',
          'compromised',
        ],
        cards: ['pay_system', 'payment_method', 'card_last4', 'expiration_date', 'card_brand', 'default_card'],
        password_sharing: ['session', '3_days', '7_days', '30_days', '1_year'],
        tickets: ['id', 'ticket_owner', 'ticket_assignee', 'magic_link', 'status', 'actions'],
        users: ['id', 'email'],
        extraData: ['field_key', 'field_value', 'actions'],
      },
      filters: [],
      options: {
        skin: 'table-p-1',
        headings: {
          id: '#',
          full_name: i18n.t('users.full_name'),
          email: i18n.t('users.email'),
          created_at: i18n.t('form.created_at'),
          subject: i18n.t('users.editing.subject'),
          status: i18n.t('form.status'),
          name: i18n.t('form.name'),
          users_count: i18n.t('groups.number_of_users'),
          type: i18n.t('groups.group_type'),
          active: i18n.t('form.active'),
          pay_system: i18n.t('transactions.payment_system'),
          amount: i18n.t('transactions.amount'),
          processing_fees: i18n.t('transactions.processing_fees'),
          processing_vat: i18n.t('transactions.vat'),
          actions: i18n.t('users.editing.action'),
          subscription_end_date: i18n.t('users.editing.ends'),
          subscription_start_date: i18n.t('users.editing.starts'),
          'plan.title': i18n.t('users.editing.plan'),
          title: i18n.t('form.title'),
          expiration_date: i18n.t('users.editing.expiration_date'),
          card_last4: i18n.t('users.editing.number'),
          card_brand: '',
          subscription_title: i18n.t('form.title'),
          autorenew: i18n.t('subscripions.autorenew'),
          is_allowed_next_subscription: i18n.t('form.next_subscription'),
          is_trial: i18n.t('subscripions.is_trial'),
          is_assigned_by_admin: i18n.t('subscription.is_assigned_by_admin'),
          default_card: '',
          session: i18n.t('sessions.session_id'),
          '3_days': `${i18n.t('users.editing.last')} ${'3_days'.replace(/1|_/g, ' ')}`,
          '7_days': `${i18n.t('users.editing.last')} ${'7_days'.replace(/1|_/g, ' ')}`,
          '30_days': `${i18n.t('users.editing.last')} ${'30_days'.replace(/1|_/g, ' ')}`,
          '1_year': `${i18n.t('users.editing.last')} ${'1_year'.replace(/1|_/g, ' ')}`,
          field_key: i18n.t('form.key'),
          field_value: i18n.t('form.value'),
          payment_method: i18n.t('transactions.payment_method'),
          'subscription.title': i18n.t('form.title'),
          'subscription.type': i18n.t('users.editing.subscription_type'),
          slots: i18n.t('users.editing.slots'),
          is_paying_by_sponsor: i18n.t('users.editing.is_paying_by_sponsor'),
        },
        sortable: [],
        filterable: false,
        requestFunction: function (data) {
          return axios
            .get(this.url, {
              params: {
                limit: data.limit,
                page: data.page,
              },
            })
            .catch((e) => {
              // this.$noty.error(e.error_description);
              this.dispatch('error', e);
            });
        },
        responseAdapter: function (resp) {
          let data = this.getResponseData(resp);
          let items = [];
          if (Array.isArray(data.data.items)) {
            items = data.data.items.map((item) => {
              if (item.processing_fee_payment !== 0 && item.processing_fee_payment !== 0) {
                item.processing_fees = item.processing_fee_payment + item.processing_fee_wallkit;
              } else {
                item.processing_fees = item.fee + item.connect_fee;
              }
              return item;
            });
          }

          const total_items = _.get(data, 'data.paginator.total_items', []);

          return {
            data: items,
            count: total_items,
          };
        },
      },
      isLoadingAssignUsers: false,
      showDeleteExtraDataModal: false,
      showRemoveTicketModal: false,
      showDeleteTicketModal: false,
      deleteExtraDataObj: {
        group_key: null,
        field_key: null,
      },
      removableTicket: {
        id: null,
        index: null,
      },
      deleteTicketObject: {
        id: null,
        index: null,
      },
      isMailchimpActive: false,
      isCampaignMonitorActive: false,
      stripeConnectAccountIdLive: null,
      stripeConnectAccountIdTest: null,
      paymentsInLiveMode: null,
    };
  },
  components: {
    DatePickerInput,
    FormFieldInput,
    formFields,
    TransactionView,
    Switchers,
    JsonPrettyTable,
    ColumnReceiptActions,
  },
  computed: {
    id() {
      return this.$route.params.id || '';
    },
    hide_activity_button() {
      return process.env.VUE_APP_BASE_URL === 'https://api.wallkit.net/api/v1';
    },
  },
  created() {
    this.fields = this.inputs;
    this.debounceGetUserCampaignMonitor = debounce(this.getUserCampaignMonitor, 500);
    this.debounceUpdateCampaignMonitorSubscriptions = debounce(
      this.updateCampaignMonitorSubscriptions,
      500,
    );
    if (this.id) {
      this.getUser();
      this.getEvents();
      this.getUserActivity();
      this.getExtraData();
      this.getUserTickets();
      this.getUserMailchimp();
      this.getMailchimpCategories();
      this.getUserCampaignMonitor();
    }
    this.transactionFields = transactionFields;
    this.debounceGetOptions = debounce(this.getOptions, 500);
    this.debounceSendUserData = debounce(this.sendUserData, 1000);
    // this.debounceGetOptions('/admin/teams?limit=100', null, null, 'name', 'id');
    this.getTeams();
    this.debounceGetOptions('/admin/users?limit=20', null, null, 'email', 'id');
    //  getOptions(url, filter_field = null, query = null, label, value, returnId)
  },
  methods: {
    loadExtraDataGroupList(value) {
      this.$set(this.isSelectLoading, 'group', true);
      const params = { needle: value };
      try {
        this.getData(`resources/users/extra/data/groups`, { params })
          .then(({ data }) => {
            this.extraData.groupsKeys = data.items;
          })
          .then(() => {
            this.$set(this.isSelectLoading, 'group', false);
          });
      } catch (e) {
        console.log(e);
        this.$set(this.isSelectLoading, 'group', false);
      }
    },
    loadExtraDataGroupFieldList(value) {
      this.$set(this.isSelectLoading, 'field', true);
      const params = { group_key: this.extraData.prefillData.group_key, needle: value };
      try {
        this.getData(`resources/users/extra/data/fields`, { params })
          .then(({ data }) => {
            this.extraData.groupFields = data.items;
          })
          .then(() => {
            this.$set(this.isSelectLoading, 'field', false);
          });
      } catch (e) {
        console.log(e);
        this.$set(this.isSelectLoading, 'field', false);
      }
    },
    isCheckboxDisabled(ch) {
      return ch.confirmed_opt || ch.updating || this.checkDependencyRules(ch);
    },
    checkDependencyRules(ch) {
      return (
        ch.unsubscribe_settings &&
        !!this.disabledDependencyLists.ids.length &&
        !this.disabledDependencyLists.ids.includes(ch.id) &&
        this.disabledDependencyLists.clients.includes(ch.client_name)
      );
    },
    handleUserSubscription(val, ch) {
      if (ch.unsubscribe_settings) {
        if (!this.disabledDependencyLists.ids.includes(ch.id)) {
          this.disabledDependencyLists.clients.push(ch.client_name);
          this.disabledDependencyLists.ids.push(ch.id);
        } else {
          pull(this.disabledDependencyLists.ids, ch.id);
          pull(this.disabledDependencyLists.clients, ch.client_name);
        }
      }
      if (val) {
        this.campaignMonitorNewSubscriptions.lists.push(ch.list_id);
        this.campaignMonitorOldSubscriptions.lists =
          this.campaignMonitorOldSubscriptions.lists.filter((list) => list !== ch.list_id);
      } else {
        this.campaignMonitorNewSubscriptions.lists =
          this.campaignMonitorNewSubscriptions.lists.filter((list) => list !== ch.list_id);
        this.campaignMonitorOldSubscriptions.lists.push(ch.list_id);
      }
    },
    isObjectEmpty(obj) {
      return isEmpty(obj);
    },
    normalizeKey(key) {
      return key.split('_').join(' ');
    },
    getUser() {
      this.loading = true;
      this.getData(`users/${this.$route.params.id}`, null, true).then(({ data }) => {
        this.createPrefillData(data);
        this.loading = false;
        this.$forceUpdate();
      });
    },
    openClearUserAccountModal(emit, id) {
      this.$bus.$emit(emit, id);
    },
    cleanUpUserData(id, modal) {
      this.deleteData(`users/${id}/data`, null, false)
        .then((resp) => {
          if (modal) {
            this.$bus.$emit(`close${modal}Modal`);
          }
          if (resp.status === 200 && resp.data.status === 'success') {
            this.$router.push({ path: '/users' });
          }
        })
        .catch((err) => console.log(err));
    },
    openAssignTicketModal(emit, id) {
      this.ticketId = id;
      this.$bus.$emit(emit, id);
    },
    assignTicket(url, params, client = false, name = null, modal, reset_field) {
      this.putData(url, params).then((resp) => {
        if (resp) {
          if (client) {
            this.getUserTickets();
          } else {
            this.refreshTable(name);
          }
          if (modal) {
            this.$bus.$emit(`close${modal}Modal`);
          }
          if (reset_field) {
            this[reset_field] = {};
            this.$forceUpdate();
          }
        }
      });
    },
    createPrefillData(data) {
      this.prefillParams = data;
      this.prefillParams.country = { name: data.country, value: data.country };
      this.prefillParams.state = { name: data.state, value: data.state };
      if (data.language) {
        this.prefillParams.language = { name: data.language.title, value: data.language.id };
      } else {
        this.prefillParams.language = null;
      }
      this.prefillParams.tags = data.tags.map((tag) => {
        return {
          name: tag,
          value: tag,
        };
      });
      if (data.settings) {
        this.prefillParams.never_lock = data.settings.never_lock;
        this.prefillParams.never_compromised = data.settings.never_compromised;
      } else {
        this.prefillParams.never_lock = false;
        this.prefillParams.never_compromised = false;
      }
      this.teams.items = data.teams;
      this.handleTableContent('teams', this.teams);
    },
    getUserActivity() {
      this.loadingActivity = true;
      this.getData(`users/${this.id}/statistic`, null, false)
        .then(({ data }) => {
          this.activity = data;
          this.loadingActivity = false;
        })
        .catch((e) => {
          console.log(e);
          this.loadingActivity = false;
        });
    },

    getExtraData(data = null) {
      if (data !== null) {
        this.extraData.groupsObj = data.items;
        this.extraData.groupsKeys = Object.keys(data.items);
        return;
      }
      this.extraData.loading = true;
      this.getData(`/users/${this.id}/extra/data`).then(({ data }) => {
        this.extraData.groupsObj = data.items;
        this.extraData.groupsKeys = Object.keys(data.items);
        this.extraData.loading = false;
        this.$forceUpdate();
      });
    },

    getEvents() {
      this.getData(`ti-events`, { params: { limit: 200 } }, false).then(({ data }) => {
        this.tieventsItems = data.items;
      });
    },
    getTeams() {
      this.getData(`teams`, { params: { limit: 200, filter: { active: true } } }, false).then(
        ({ data }) => {
          this.teamsItems = data.items.map((option) => {
            return {
              label: option.name,
              value: option.id,
            };
          });
        },
      );
    },
    getTickets() {
      this.getData(`ti-event/${this.tievents.id}`, null, false).then(({ data }) => {
        this.tievents_tickets = '';
        this.tievents_tickets_items = data.tickets;
      });
    },
    assignTicketToUser(modal) {
      // buy ticket
      let params = {
        owner_id: this.id,
        quantity: 1,
      };
      this.postData(`ti-event/ticket/${this.tievents_tickets.id}/passes`, params).then(
        ({ data }) => {
          this.getUserTickets();
          this.$bus.$emit(`close${modal}Modal`);
        },
      );

      // get passed ticket
      // assign passed ticket to user ${this.id}
    },
    getUserTickets() {
      this.getData(`ti-event/ticket/user/${this.id}/passes`, null, false).then(({ data }) => {
        this.tievents_for_ticket_table = data;
      });
    },
    async getMailchimpCategories() {
      try {
        const {
          data: { groups },
        } = await this.getData(`integrations/mailchimp/categories`, null, false, true);
        this.mailchimp_categories = Array.isArray(groups) ? groups : [];
      } catch (err) {
        console.log(err);
      }
    },
    async getUserMailchimp() {
      try {
        const {
          data: { interests },
        } = await this.getData(
          `integrations/mailchimp/user/${this.id}/interests`,
          null,
          false,
          true,
        );

        if (Array.isArray(interests)) {
          interests.forEach((item) => {
            if (item) {
              this.mailchimp_interests[item.id] = item;
            }
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    async getUserCampaignMonitor() {
      try {
        this.campaign_monitor_loading = true;

        const {
          data: { lists },
        } = await axios.get('/admin/integrations/campaignmonitor/admin-interests-lists', {
          params: { user_id: this.id },
        });
        const {
          data: { items },
        } = await axios.get('/admin/integrations/campaignmonitor/admin-lists');
        this.campaign_monitor = items;
        this.prefillSubscriptions(lists);
      } catch (err) {
        this.campaign_monitor_loading = false;
        console.log(err);
      }
    },
    prefillSubscriptions(userSubs) {
      this.campaign_monitor.map((item) => {
        userSubs.map((list) => {
          if (item.list_id === list.list_id) {
            item.subscribed = list.subscribed;
            item.updating = list.updating;
          }
        });
      });

      this.campaign_monitor = orderBy(this.campaign_monitor, 'list_name');

      this.campaign_monitor_loading = false;
      this.campaignMonitorNewSubscriptions.lists.length = 0;
      this.campaignMonitorOldSubscriptions.lists.length = 0;

      this.disabledDependencyLists = {
        ids: [],
        clients: [],
      };
    },
    async updateCampaignMonitorSubscriptions() {
      try {
        this.campaign_monitor_loading = true;
        let lists = [];
        if (!!this.campaignMonitorNewSubscriptions.lists.length) {
          const { data } = await axios.post(`admin/integrations/campaignmonitor/subscribe`, {
            user_id: this.id,
            lists: this.campaignMonitorNewSubscriptions.lists,
          });
          lists = data.lists;
        }
        if (!!this.campaignMonitorOldSubscriptions.lists.length) {
          const { data } = await axios.post(`admin/integrations/campaignmonitor/unsubscribe`, {
            user_id: this.id,
            lists: this.campaignMonitorOldSubscriptions.lists,
          });
          lists = data.lists;
        }
        this.prefillSubscriptions(lists);
      } catch (err) {
        this.campaign_monitor_loading = false;
      }
    },
    reverseObject(object) {
      let newObject = {};
      let keys = [];
      for (let key in object) {
        keys.push(key);
      }
      for (let i = keys.length - 1; i >= 0; i--) {
        newObject[keys[i]] = object[keys[i]];
      }
      return newObject;
    },
    transformKey(key) {
      return `${this.$t('users.editing.last')} ${key.replace(/1|_/g, ' ')}`;
    },
    handleTableContent(name, data) {
      if (data.items) {
        this.tableItems[name] = data.items;
        this.isTableEmpty[name] = _.isEmpty(data.items);
      } else {
        this.tableItems[name] = [];
        this.isTableEmpty[name] = true;
      }
      this.$forceUpdate();
    },
    postUserData(url, params, client = false, name = null, modal, reset_field) {
      this.postData(url, params).then((resp) => {
        if (resp) {
          if (client) {
            this.getUser();
          } else {
            this.refreshTable(name);
          }
          if (modal) {
            this.$bus.$emit(`close${modal}Modal`);
          }
          if (reset_field) {
            this[reset_field] = {};
            this.$forceUpdate();
          }
        }
      });
    },
    sendUserData(data) {
      this.formValidated = true;

      if (this.$refs.form.checkValidity()) {
        this.paramsMutated = data;
        this.paramsMutated['language_id'] = this.paramsMutated.language;
        if (this.editing) {
          this.putData(`users/${this.prefillParams.id}`, this.paramsMutated);
        } else {
          this.postData('users', this.paramsMutated).then(({ data: { id } }) => {
            this.$router.push({ path: `/users/edit/${id}` });
          });
        }
      }
    },
    toLowerCase(string) {
      return string.toLowerCase();
    },
    toUpperCaseFirstLetter(string) {
      if (!string || typeof string !== "string") return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    submit(name) {
      this.$bus.$emit(`submit-${name}-form`);
    },
    openEditSubscriptionModal(emit, date, autorenew, id, is_allowed_next_subscription, is_trial) {
      this.$bus.$emit(emit, id);
      this.$set(
        this.subscription,
        'subscription_end_date',
        new Date(formattedDateForBrowsers(date, true)),
      );
      this.$set(this.subscription, 'autorenew', autorenew || false);
      this.$set(
        this.subscription,
        'is_allowed_next_subscription',
        is_allowed_next_subscription || false,
      );
      this.$set(this.subscription, 'is_trial', is_trial || false);
    },
    submitEditSubscription(subscrId) {
      let subscriptionEndDate = new Date(this.subscription.subscription_end_date).toDateString();
      subscriptionEndDate = subscriptionEndDate.replaceAll('/', '.');
      this.putData(
        `users/${this.id}/subscriptions/${subscrId}`,
        { ...this.subscription, subscription_end_date: subscriptionEndDate },
        null,
        true,
      ).then((resp) => {
        if (resp) {
          this.refreshTable('subscriptions');
          this.$bus.$emit('closeEditSubscriptionModal');
        }
      });
    },
    ticketTableDateFormat(date) {
      let dateArray = date.split(' ');
      if (dateArray[0]) {
        dateArray = dateArray[0].split('-');
        if (dateArray.length > 2) {
          return dateArray[1] + '/' + dateArray[2] + '/' + dateArray[0];
        }
      }
      return NO_TIME_SYMBOL;
    },
    async onAssignUsersSearch(value) {
      this.isLoadingAssignUsers = true;
      await this.debounceGetOptions(`/admin/users?limit=20`, 'users.email', value, 'email', 'id')
        .catch((error) => {
          console.log('get assign users error', error);
        })
        .finally(() => {
          this.isLoadingAssignUsers = false;
        });
    },

    openCreateExtraDataModal(emit) {
      this.clearExtraDataPrefillData();
      this.$bus.$emit(emit);
    },
    openEditExtraDataModal(emit, group_key, field_key, field_value) {
      this.clearExtraDataPrefillData();
      this.$bus.$emit(emit);
      this.deleteExtraDataObj.group_key = group_key;
      this.deleteExtraDataObj.field_key = field_key;
      this.extraData.prefillData.group_key = group_key;
      this.extraData.prefillData.field_key = field_key;
      this.extraData.prefillData.field_value = field_value;
    },
    submitExtraData(mode) {
      for (let key in this.extraData.prefillData) {
        if (this.extraData.prefillData[key] === null) {
          this.$noty.error('Invalid extra data');
          return;
        }
      }

      this.putData(
        `users/${this.id}/extra/data`,
        { extra_groups: [this.extraData.prefillData] },
        null,
        false,
      ).then((resp) => {
        if (resp) {
          this.getExtraData(resp.data);
          this.$bus.$emit('closeCreateExtraDataModal');
        }
      });
    },

    clearExtraDataPrefillData() {
      this.clearDeleteExtraData();
      this.extraData.prefillData.group_key = null;
      this.extraData.prefillData.field_key = null;
      this.extraData.prefillData.field_value = null;
    },

    openDeleteExtraDataModal(group_key, field_key) {
      this.deleteExtraDataObj.group_key = group_key;
      this.deleteExtraDataObj.field_key = field_key;
      this.showDeleteExtraDataModal = true;
    },

    openRemoveTicketModal(id, index) {
      this.removableTicket.id = id;
      this.removableTicket.index = index;
      this.showRemoveTicketModal = true;
    },

    openDeleteTicketModal(id, index) {
      this.deleteTicketObject.id = id;
      this.deleteTicketObject.index = index;
      this.showDeleteTicketModal = true;
    },
    async deleteExtraData() {
      let payload = {
        extra_groups: [
          {
            group_key: this.deleteExtraDataObj.group_key,
            field_key: this.deleteExtraDataObj.field_key,
          },
        ],
      };
      await this.deleteData(`users/${this.id}/extra/data`, false, null, { data: payload })
        .then(() => {
          this.getExtraData();
          this.clearDeleteExtraData();
        })
        .catch((error) => {
          console.log('delete extra data error', error);
        })
        .finally(() => {
          this.showDeleteExtraDataModal = false;
        });
    },

    async removeTicket() {
      await this.deleteData(`ti-event/ticket/pass/${this.removableTicket.id}/detach-assign`)
        .then(() => {
          this.getUserTickets();
          this.clearRemovableTicket();
        })
        .catch((error) => {
          console.log('remove ticket error', error);
        })
        .finally(() => {
          this.showRemoveTicketModal = false;
        });
    },

    async deleteTicket() {
      await this.deleteData(`ti-event/ticket/pass/${this.deleteTicketObject.id}`)
        .then(() => {
          this.getUserTickets();
          this.clearDeleteTicket();
        })
        .catch((error) => {
          console.log('delete ticket error', error);
        })
        .finally(() => {
          this.showDeleteTicketModal = false;
        });
    },

    async regenerateAuthLinkForTicket(passId) {
      await this.putData(`ti-event/ticket/pass/${passId}/generate-auth-token`)
        .then(() => {
          this.getUserTickets();
        })
        .catch((error) => {
          console.log('regenerate auth link ticket error', error);
        });
    },

    async deleteAuthLinkForTicket(passId) {
      await this.deleteData(`ti-event/ticket/pass/${passId}/delete-auth-token`)
        .then(() => {
          this.getUserTickets();
        })
        .catch((error) => {
          console.log('regenerate auth link ticket error', error);
        });
    },

    clearDeleteExtraData() {
      this.deleteExtraDataObj.group_key = null;
      this.deleteExtraDataObj.field_key = null;
      this.showDeleteExtraDataModal = false;
    },

    clearDeleteTicket() {
      this.deleteTicketObject.id = null;
      this.deleteTicketObject.index = null;
      this.showDeleteTicketModal = false;
    },

    clearRemovableTicket() {
      this.removableTicket.id = null;
      this.removableTicket.index = null;
      this.showRemoveTicketModal = false;
    },

    getMergedArrays(arr1, arr2, param) {
      let uniqueArr2 = [];
      if (arr1.length > 0) {
        for (let item2 in arr2) {
          if (item2[param]) {
            let duplicated = false;
            for (let item1 in arr1) {
              if (item1[param] && item2[param] === item1[param]) {
                duplicated = true;
                break;
              }
            }
            if (!duplicated) {
              uniqueArr2.push(item2);
            }
          }
        }
      } else {
        uniqueArr2 = arr2;
      }
      return arr1.concat(uniqueArr2);
    },
    async checkIntegrations() {
      try {
        const response = await this.getData(`resources/${this.$store.getters['resource/currentResource'].key}`);

        this.isMailchimpActive = !!get(response, 'data.settings.mailchimp.key', false);
        this.isCampaignMonitorActive = !!get(response, 'data.settings.campaignmonitor.oauth.access_token', false);
        this.stripeConnectAccountIdLive = get(response, 'data.settings.stripe_connect.live.stripe_user_id');
        this.stripeConnectAccountIdTest = get(response, 'data.settings.stripe_connect.test.stripe_user_id');
        this.paymentsInLiveMode = get(response, 'data.settings.payments_in_live_mode');

        if(this.stripeConnectAccountIdLive || this.stripeConnectAccountIdTest) {

          this.$set(this.options.headings, 'customer_link', i18n.t('transactions.customer_link'));
          const cardsColumns = [...this.columns.cards]
          cardsColumns.push('customer_link');
          this.$set(this.columns, 'cards', cardsColumns);

          this.$set(this.options.headings, 'refund', i18n.t('transactions.refund'));
          const transactionsColumns = [...this.columns.transactions]
          transactionsColumns.push('refund');
          this.$set(this.columns, 'transactions', transactionsColumns);

        }

      } catch (e) {
        console.log(e);
      }
    },
    goToCustomerPage(data) {

      const customerId = get(data, 'customer_id');
      const mode = get(data, 'payment_method.mode');

      let isTest = null;

      switch (mode) {
        case 'test':
          isTest = true;
          break;
        case 'live':
          isTest = false;
          break;
        default:
          isTest = this.paymentsInLiveMode;
      }

      const isRoot = this.$store.getters['auth/role'] === "root";
      const testSlug = isTest ? '/test' : '';

      let url = `https://dashboard.stripe.com${testSlug}`;

      if (isTest){
        if (isRoot) {
          url += `/connect/accounts/${this.stripeConnectAccountIdTest}/customers/${customerId}`;
        } else {
          url += `/customers/${customerId}`;
        }
      } else {
        if (isRoot) {
          url += `/connect/accounts/${this.stripeConnectAccountIdLive}/customers/${customerId}`;
        } else {
          url += `/customers/${customerId}`;
        }
      }

      window.open(url, '_blank');

    },
    goToTransactionPage(data) {

      const transactionId = get(data, 'transaction_id');
      let isTest = !get(data, 'livemode', false);
      const isRoot = this.$store.getters['auth/role'] === "root";
      const testSlug = isTest ? '/test' : '';

      let url = `https://dashboard.stripe.com${testSlug}`;

      if (isTest){
        if (isRoot) {
          url += `/connect/accounts/${this.stripeConnectAccountIdTest}/payments/${transactionId}`;
        } else {
          url += `/payments/${transactionId}`;
        }
      } else {
        if (isRoot) {
          url += `/connect/accounts/${this.stripeConnectAccountIdLive}/payments/${transactionId}`;
        } else {
          url += `/payments/${transactionId}`;
        }
      }

      window.open(url, '_blank');

    },
    getSponsoredSubscriptionsInput(sponsoredSubscriptionType) {
      return {
        name: 'subscription_id',
        label: i18n.t('form.subscriptions_id'),
        placeholder: i18n.t('transactions.type_to_search'),
        filter_field: 'subscriptions.title',
        static_filter: { 'subscriptions.type': [sponsoredSubscriptionType] },
        searchable: true,
        group: true,
        allowEmpty: false,
        preselectFirst: true,
        required: true,
        options_url: `plans?limit=100`,
      };
    },
  },
  destroyed() {
    this.$bus.$off('updateUserTags');
  },
  watch: {
    disabledDependencyLists() {
      this.$forceUpdate();
    },
    tievents() {
      this.getTickets();
    },
    'extraData.prefillData.group_key': function () {
      this.loadExtraDataGroupFieldList();
    },
    sponsoredSubscriptionType: function () {
      this.sponsoredSubscription = {};
    },
  },
  mounted() {
    this.checkIntegrations();
  },
};
</script>
